import { 
  FileText, 
  Star,
  Clock,
  TrendingUp,
  Users,
  Plus,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import type { TeamKnowledgeBaseEntry } from "@/types/team";
import { cn } from "@/lib/utils";

interface ConfluenceHomeProps {
  entries: TeamKnowledgeBaseEntry[];
  selectedCategory: string | null;
  onEntrySelect: (entryId: string) => void;
  onCreateNew: () => void;
  canEdit: boolean;
}

export const ConfluenceHome = ({
  entries,
  selectedCategory,
  onEntrySelect,
  onCreateNew,
  canEdit
}: ConfluenceHomeProps) => {
  const filteredEntries = selectedCategory
    ? entries.filter(e => e.category === selectedCategory)
    : entries;

  const starredEntries = filteredEntries.filter(e => e.is_pinned);
  const recentEntries = [...filteredEntries]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 8);

  // Group by category
  const entriesByCategory = entries.reduce((acc, entry) => {
    const cat = entry.category || 'Allgemein';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(entry);
    return acc;
  }, {} as Record<string, TeamKnowledgeBaseEntry[]>);

  if (entries.length === 0) {
    return (
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="text-center">
            <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-[#DEEBFF] dark:bg-primary/10 flex items-center justify-center mx-auto mb-4 md:mb-6">
              <Sparkles className="h-8 w-8 md:h-12 md:w-12 text-[#0052CC] dark:text-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2 md:mb-3">
              Willkommen in der Wissensbank
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-md mx-auto mb-6 md:mb-8 px-4">
              Erstelle deine erste Seite, um Wissen mit deinem Team zu teilen.
            </p>
            {canEdit && (
              <Button 
                onClick={onCreateNew}
                size="lg"
                className="bg-[#0052CC] hover:bg-[#0747A6] dark:bg-primary dark:hover:bg-primary/90"
              >
                <Plus className="h-5 w-5 mr-2" />
                Erste Seite erstellen
              </Button>
            )}
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="flex-1 bg-[#FAFBFC] dark:bg-background">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-xl md:text-[28px] font-semibold text-foreground mb-1 md:mb-2">
            {selectedCategory || 'Wissensbank'}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {selectedCategory 
              ? `${filteredEntries.length} Seite${filteredEntries.length !== 1 ? 'n' : ''} in dieser Kategorie`
              : 'Finde Anleitungen, Best Practices und wichtige Informationen für dein Team.'}
          </p>
        </div>

        {/* Quick Stats - Only on overview */}
        {!selectedCategory && (
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6 md:mb-8">
            <Card className="bg-white dark:bg-card border-0 shadow-sm">
              <CardContent className="p-3 md:p-4">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 text-center md:text-left">
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg bg-[#0052CC]/10 dark:bg-primary/10 flex items-center justify-center">
                    <FileText className="h-4 w-4 md:h-5 md:w-5 text-[#0052CC] dark:text-primary" />
                  </div>
                  <div>
                    <p className="text-lg md:text-2xl font-bold text-foreground">{entries.length}</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Seiten</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-card border-0 shadow-sm">
              <CardContent className="p-3 md:p-4">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 text-center md:text-left">
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Star className="h-4 w-4 md:h-5 md:w-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-lg md:text-2xl font-bold text-foreground">{starredEntries.length}</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Favoriten</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-card border-0 shadow-sm">
              <CardContent className="p-3 md:p-4">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 text-center md:text-left">
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-lg md:text-2xl font-bold text-foreground">
                      {Object.keys(entriesByCategory).length}
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground">Kategorien</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Starred Pages */}
        {starredEntries.length > 0 && (
          <section className="mb-6 md:mb-8">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <Star className="h-4 w-4 md:h-5 md:w-5 text-amber-500" />
              <h2 className="text-base md:text-lg font-semibold text-foreground">Wichtige Seiten</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              {starredEntries.slice(0, 4).map(entry => (
                <PageCard
                  key={entry.id}
                  entry={entry}
                  onClick={() => onEntrySelect(entry.id)}
                  showStar
                />
              ))}
            </div>
          </section>
        )}

        {/* Recent Pages */}
        <section className="mb-6 md:mb-8">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <Clock className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
            <h2 className="text-base md:text-lg font-semibold text-foreground">Zuletzt aktualisiert</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
            {recentEntries.map(entry => (
              <PageCard
                key={entry.id}
                entry={entry}
                onClick={() => onEntrySelect(entry.id)}
                compact
              />
            ))}
          </div>
        </section>

        {/* All Pages by Category */}
        {!selectedCategory && (
          <section>
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <Users className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
              <h2 className="text-base md:text-lg font-semibold text-foreground">Nach Kategorie</h2>
            </div>
            <div className="space-y-2 md:space-y-3">
              {Object.entries(entriesByCategory).map(([category, categoryEntries]) => (
                <Card key={category} className="bg-white dark:bg-card border-0 shadow-sm overflow-hidden">
                  <CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary" 
                          className="bg-[#DEEBFF] text-[#0052CC] dark:bg-primary/20 dark:text-primary border-0 text-xs"
                        >
                          {category}
                        </Badge>
                        <span className="text-xs md:text-sm text-muted-foreground">
                          {categoryEntries.length} Seite{categoryEntries.length !== 1 ? 'n' : ''}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-3 md:px-6 pb-3 md:pb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 md:gap-2">
                      {categoryEntries.slice(0, 6).map(entry => (
                        <button
                          key={entry.id}
                          onClick={() => onEntrySelect(entry.id)}
                          className="flex items-center gap-2 p-2 rounded-md hover:bg-[#F4F5F7] dark:hover:bg-muted text-left text-xs md:text-sm transition-colors group"
                        >
                          <FileText className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#0052CC]/60 dark:text-primary/60 flex-shrink-0" />
                          <span className="truncate text-foreground group-hover:text-[#0052CC] dark:group-hover:text-primary">
                            {entry.title}
                          </span>
                          {entry.is_pinned && (
                            <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                    {categoryEntries.length > 6 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-[#0052CC] dark:text-primary hover:text-[#0052CC] dark:hover:text-primary text-xs md:text-sm"
                      >
                        Alle {categoryEntries.length} Seiten anzeigen
                        <ArrowRight className="h-3 w-3 md:h-4 md:w-4 ml-1" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </ScrollArea>
  );
};

// Page Card Component
interface PageCardProps {
  entry: TeamKnowledgeBaseEntry;
  onClick: () => void;
  compact?: boolean;
  showStar?: boolean;
}

const PageCard = ({ entry, onClick, compact, showStar }: PageCardProps) => {
  const excerpt = entry.content
    .replace(/<[^>]*>/g, '')
    .slice(0, compact ? 60 : 120);

  return (
    <button
      onClick={onClick}
      className={cn(
        "text-left bg-white dark:bg-card rounded-lg border-0 shadow-sm hover:shadow-md transition-all group w-full",
        compact ? "p-2.5 sm:p-3" : "p-3 sm:p-4"
      )}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        <div className={cn(
          "rounded-lg bg-[#DEEBFF] dark:bg-primary/10 flex items-center justify-center flex-shrink-0",
          compact ? "h-7 w-7 sm:h-8 sm:w-8" : "h-8 w-8 sm:h-10 sm:w-10"
        )}>
          <FileText className={cn(
            "text-[#0052CC] dark:text-primary",
            compact ? "h-3.5 w-3.5 sm:h-4 sm:w-4" : "h-4 w-4 sm:h-5 sm:w-5"
          )} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={cn(
            "font-medium text-foreground group-hover:text-[#0052CC] dark:group-hover:text-primary truncate",
            compact ? "text-xs sm:text-sm" : "text-sm sm:text-base"
          )}>
            {entry.title}
          </h3>
          {!compact && (
            <>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {excerpt}...
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                {entry.category && (
                  <Badge variant="secondary" className="text-xs bg-muted border-0">
                    {entry.category}
                  </Badge>
                )}
                <span>
                  {formatDistanceToNow(new Date(entry.updated_at), { 
                    addSuffix: true, 
                    locale: de 
                  })}
                </span>
              </div>
            </>
          )}
          {compact && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDistanceToNow(new Date(entry.updated_at), { 
                addSuffix: true, 
                locale: de 
              })}
            </p>
          )}
        </div>
        {showStar && entry.is_pinned && (
          <Star className="h-4 w-4 text-amber-500 fill-amber-500 flex-shrink-0" />
        )}
      </div>
    </button>
  );
};

export default ConfluenceHome;
